import React, { useEffect, useRef, useMemo, useCallback } from "react";
import { Stage as PixiStage, Graphics } from "@pixi/react";
import { ReactReduxContext } from "react-redux";
import { DimensionsContext } from "./Contexts/DimensionsContext";
import { AppActivityContext } from "./Contexts/AppActivityContext";
import { ReelControlContext } from "./Contexts/ReelControlContext";
import { FreeSpinControlContext } from "./Contexts/FreeSpinControlContext";
import { HoldAndWinControlContext } from "./Contexts/HoldAndWinControlContext";
import { RoyalCollectControlContext } from "./Contexts/RoyalCollectControlContext";
import { PotCollectionControlContext } from "./Contexts/PotCollectionControlContext";
import SlotRegistryContext from "./Contexts/SlotRegistryContext";

import { useDimensions } from "./Contexts/DimensionsContext";
import ContextBridge from "./Contexts/ContextBridge";
import BackgroundAnimation from "./Components/Animations/BackgroundAnimation/BackgroundAnimation";
import GameView from "./Components/Animations/GameView/GameView";

// import Menu from "./Components/UserInterface/primary/Menu/Menu";
// import IntroPopup from "./Components/UserInterface/shared/Popups/IntroPopup/IntroPopup/IntroPopup";
import IntroPopup from "./Components/UserInterface/shared/Popups/IntroPopup/IntroPopup/IntroPopupComposed";
import IntroTransitionOverlay from "./Components/Animations/IntroTransitionOverlay/IntroTransitionOverlay";
import { isStandaloneReplayMode } from "./utils/launcherOptions";
import ErrorRecoveryOverlay from "./Components/Animations/ErrorRecoveryOverlay/ErrorRecoveryOverlay";
import Transitions from "./Components/Animations/Transitions/Transitions";
import GameContainer from "./Games/Main/GameContainer/GameContainer";
import WinAnimationPrimary from "./Components/Animations/WinAnimation/WinAnimation";
import WinAnimationSecondary from "./Components/Animations/WinAnimation/WinAnimationSecondary";
import IosFullscreenOverlay from "./Components/Animations/IosFullscreenOverlay/IosFullscreenOverlay";
import { FEATURES_CONFIG } from "./featuresConfig";
import Dialogs from "./Components/Animations/Dialogs/Dialogs";
import { ReelRefsContext } from "./Contexts/ReelRefsContext";
import HookImport from "./Hooks/HookImport";
import PrimaryLoader from "./Components/UserInterface/primary/Other/Loader";
import SecondaryLoader from "./Components/UserInterface/secondary/Popups/Loader";
import { useSelector } from "react-redux";
import { Settings } from "./settings";

// Dynamic UI Theme imports
import PrimaryUserInterface from "./Components/UserInterface/primary/UserInterface";
import SecondaryUserInterface from "./Components/UserInterface/secondary/UserInterface";

const UserInterface = Settings.APP.UI_THEME === "primary"
  ? PrimaryUserInterface
  : SecondaryUserInterface;

const Loader = Settings.APP.UI_THEME === "primary"
  ? PrimaryLoader
  : SecondaryLoader;

const WinAnimation = Settings.APP.UI_THEME === "primary"
  ? WinAnimationPrimary
  : WinAnimationSecondary;

const StageWithDimensions = ({ children }) => {
  const { dimensions } = useDimensions();
  const stageRef = useRef(null);

  // Memoize contexts array (static array, doesn't change)
  const contexts = useMemo(
    () => [
      { Context: ReelRefsContext },
      { Context: SlotRegistryContext },
      { Context: ReactReduxContext },
      { Context: AppActivityContext },
      { Context: DimensionsContext },
      { Context: ReelControlContext },
      { Context: FreeSpinControlContext },
      { Context: HoldAndWinControlContext },
      { Context: RoyalCollectControlContext },
      { Context: PotCollectionControlContext },
    ],
    [],
  );

  // Memoize PixiStage options
  const stageOptions = useMemo(
    () => ({
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      backgroundColor: 0x000000,
      antialias: false,
      powerPreference: "high-performance",
      preserveDrawingBuffer: false,
      clearBeforeRender: true,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (stageRef.current) {
      const app = stageRef.current.app;
      globalThis.__PIXI_APP__ = app;
      globalThis.__PIXI_STAGE__ = app.stage;
      globalThis.__PIXI_RENDERER__ = app.renderer;
      globalThis.__PIXI_STAGE__.name = "stage";
      globalThis.__PIXI_STAGE__.sortableChildren = true;
    }
  }, []);

  // Keep renderer resolution in sync with devicePixelRatio
  // Covers: F12 devtools, window resize, browser zoom, monitor switching
  useEffect(() => {
    const updateResolution = () => {
      if (!stageRef.current) return;
      const renderer = stageRef.current.app.renderer;
      const newResolution = Math.min(window.devicePixelRatio || 1, 2);
      if (renderer.resolution !== newResolution) {
        renderer.resolution = newResolution;
        renderer.resize(dimensions.width, dimensions.height);
      }
    };

    // Update on dimension change
    updateResolution();

    // Also detect DPR changes independently (monitor switch, browser zoom)
    const mqString = `(resolution: ${window.devicePixelRatio}dppx)`;
    const mql = window.matchMedia(mqString);
    const handleDprChange = () => updateResolution();
    mql.addEventListener("change", handleDprChange);

    return () => mql.removeEventListener("change", handleDprChange);
  }, [dimensions.width, dimensions.height]);

  return (
    <ContextBridge
      contexts={contexts}
      render={(bridgeChildren) => (
        <PixiStage
          ref={stageRef}
          width={dimensions.width}
          height={dimensions.height}
          options={stageOptions}
          name="stage"
        >
          {bridgeChildren}
        </PixiStage>
      )}
    >
      {children}
    </ContextBridge>
  );
};

// ── Debug Crosshair Overlay ──────────────────────────────────────────────────
// Draws 1px red lines at the vertical and horizontal center of the screen.
// Controlled by FEATURES_CONFIG.DEV_TOOLS.DEBUG_CROSSHAIR.
const DebugCrosshair = () => {
  const { dimensions } = useDimensions();
  const draw = useCallback((g) => {
    g.clear();
    const cx = dimensions.width / 2;
    const cy = dimensions.height / 2;
    // Horizontal line
    g.lineStyle(1, 0xff0000, 0.8);
    g.moveTo(0, cy);
    g.lineTo(dimensions.width, cy);
    // Vertical line
    g.moveTo(cx, 0);
    g.lineTo(cx, dimensions.height);
  }, [dimensions.width, dimensions.height]);

  return <Graphics draw={draw} zIndex={99999} />;
};

const Stage = () => {
  const { loaderComplete } = useSelector((state) => state.loadingProgress);

  return (
    <>
      <StageWithDimensions>
        <HookImport />

        <Loader />

        {/* Game mounts after loader "out" finishes — loader stays frozen on top while game initializes */}
        {loaderComplete && (
          <>
            {/* GameView replaces both BackgroundAnimation and GameContainer */}
            {FEATURES_CONFIG.GAME_VIEW.ENABLED
              ? <GameView />
              : <BackgroundAnimation />
            }
            {/* Standalone replay link (docs/replay-history-spec.md §0 Stage
                9/10): no intro ceremony at all — loader fades straight into
                the game with the replay Enter modal on top. Both the intro
                popup and its black cover-and-reveal transition are skipped
                outright rather than left mounted-but-inactive, so neither
                can put a full-screen layer over a session the player can
                only ever watch one round in. */}
            {!isStandaloneReplayMode() && (
              <>
                <IntroPopup />
                <IntroTransitionOverlay />
              </>
            )}
            <ErrorRecoveryOverlay />
            <UserInterface>
              {/* GameContainer only renders when GameView is disabled */}
              {!FEATURES_CONFIG.GAME_VIEW.ENABLED && <GameContainer />}
            </UserInterface>
            <Transitions />
            <Dialogs />
            <WinAnimation />
            {FEATURES_CONFIG.IOS_FULLSCREEN_OVERLAY.ENABLED && <IosFullscreenOverlay />}
            {FEATURES_CONFIG.DEV_TOOLS.DEBUG_CROSSHAIR && <DebugCrosshair />}

          </>
        )}
      </StageWithDimensions>
    </>
  );
};

export default Stage;
